import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getBoard,
  moveRecruit,
  STAGE_COLORS,
  slaState,
  hoursSince,
  type BoardColumn,
  type Recruit,
  type Stage,
} from '../api/recruits';

const SLA_DOT: Record<'green' | 'yellow' | 'red', string> = {
  green: 'bg-emerald-400',
  yellow: 'bg-amber-400',
  red: 'bg-red-500',
};

function formatDuration(hours: number) {
  if (hours < 1) return '<1h';
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

export default function RecruitingPipeline() {
  const [board, setBoard] = useState<BoardColumn[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const navigate = useNavigate();

  async function reload() {
    try {
      const { board } = await getBoard();
      setBoard(board);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load board');
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function onDrop(toStage: Stage) {
    if (!dragId || !board) return;
    const id = dragId;
    setDragId(null);

    // Optimistic UI: move the card now, roll back on failure.
    const prev = board;
    const next = board.map((c) => ({
      ...c,
      recruits: c.recruits.filter((r) => r._id !== id),
    }));
    let moved: Recruit | undefined;
    for (const c of prev) {
      const found = c.recruits.find((r) => r._id === id);
      if (found) moved = { ...found, stage: toStage, stageEnteredAt: new Date().toISOString() };
    }
    if (moved) {
      const target = next.find((c) => c.stage === toStage);
      if (target) {
        target.recruits = [moved, ...target.recruits];
        target.count = target.recruits.length;
      }
    }
    next.forEach((c) => { c.count = c.recruits.length; });
    setBoard(next);

    try {
      await moveRecruit(id, toStage);
      reload();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Move failed';
      setError(msg);
      setBoard(prev);
    }
  }

  return (
    <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="h-display text-3xl">Pipeline</h1>
            <p className="text-dg-mist text-sm mt-1">
              Drag recruits left-to-right through the 9-stage funnel.
            </p>
          </div>
          <Link
            to="/recruiting/applications/new"
            className="bg-dg-red hover:bg-dg-red-hover text-white text-sm font-medium px-4 py-2 rounded-md transition"
          >
            + New Application
          </Link>
        </div>

        {error && (
          <div className="mb-4 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300">
            {error}
          </div>
        )}

        {!board ? (
          <div className="text-dg-mist">Loading…</div>
        ) : (
          <div
            data-testid="kanban"
            className="flex gap-3 overflow-x-auto pb-4"
          >
            {board.map((col) => (
              <div
                key={col.stage}
                data-testid={`kanban-col-${col.stage}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(col.stage)}
                className="w-72 shrink-0 bg-dg-panel/70 border border-dg-border rounded-lg flex flex-col"
              >
                <div
                  className="px-3 py-2 border-b border-dg-border flex items-center justify-between"
                  style={{ borderTop: `3px solid ${STAGE_COLORS[col.stage]}` }}
                >
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-white">
                      {col.label}
                    </div>
                    <div className="text-[10px] text-dg-mist mt-0.5">
                      SLA {col.slaHours || '—'}h
                    </div>
                  </div>
                  <div className="text-xs text-dg-mist bg-white/5 px-2 py-0.5 rounded-full">
                    {col.count}
                  </div>
                </div>
                <div className="p-2 space-y-2 flex-1 min-h-[120px]">
                  {col.recruits.map((r) => {
                    const sla = slaState(col, r.stageEnteredAt);
                    return (
                      <div
                        key={r._id}
                        data-testid={`recruit-card-${r._id}`}
                        draggable
                        onDragStart={() => setDragId(r._id)}
                        onClick={() => navigate(`/recruiting/recruits/${r._id}`)}
                        className="bg-dg-surface/80 hover:bg-dg-surface border border-dg-border rounded-md p-2.5 cursor-pointer"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-medium text-sm">
                            {r.firstName} {r.lastName}
                          </div>
                          <span className={`w-2 h-2 rounded-full mt-1.5 ${SLA_DOT[sla]}`} title={`SLA ${sla}`} />
                        </div>
                        <div className="text-[11px] text-dg-mist mt-1">
                          {r.state} · {r.licensedStatus} · {r.compPercent}%
                        </div>
                        <div className="text-[11px] text-dg-mist mt-0.5">
                          In stage {formatDuration(hoursSince(r.stageEnteredAt))}
                        </div>
                      </div>
                    );
                  })}
                  {col.recruits.length === 0 && (
                    <div className="text-[11px] text-dg-mist/60 italic px-1 py-3 text-center">
                      drop here
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
